import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Reusable ConfirmationDialog component
 * 
 * @param {Object} props - The props for the component
 * @param {React.Ref} ref - The ref to the component
 * @returns {React.ReactNode} The ConfirmationDialog component
 * @example
 * <ConfirmationDialog
 *     ref={confirmationDialogRef}
 *     title="Are you sure you want to delete this notification?"
 *     text="Are you sure you want to delete this notification?"
 *     onConfirm={() => {}}
 * />
 */
const ConfirmationDialog = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        title: "",
        text: "",
        onConfirm: () => { },
    });

    // Allow the parent to control dialog via ref
    useImperativeHandle(ref, () => ({
        openDialog: ({ title, text, onConfirm }) => {
            setDialogProps({ title, text, onConfirm });
            setOpen(true);
        },
    }));

    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirm = () => {
        dialogProps.onConfirm(); // Call the passed callback function
        setOpen(false);
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{dialogProps.title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{dialogProps.text}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    {t('Cancel')}
                </Button>
                <Button onClick={handleConfirm} color="primary" autoFocus>
                    {t('Confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default ConfirmationDialog;
